import { World, F_UNDER_CONSTRUCTION, F_CONVERTER_ON, ACT_RECLAIM } from "./world";
import { DT, OWNER_PLAYER, OWNER_ENEMY } from "./constants";
import type { Sim } from "./sim";

const RECLAIM_METAL_PER_BP = 1.2; // metal (or energy) reclaimed per build-power per second

// The streaming economy (GDD §3). Construction never halts on stall — it slows
// proportionally. Overflow past storage cap is wasted.

export function economyStep(w: World, sim: Sim): void {
  const bp = sim.buildPowerOn;
  const rp = sim.reclaimPowerOn;
  const rpair = sim.repairPowerOn;

  // --- Factory production: keep each factory working its queue. ---
  for (let e = 0; e < w.count; e++) {
    if (!w.alive[e] || w.kind[e] !== 1) continue;
    const def = w.def(e);
    if (def.role !== "factory" || w.underConstruction(e)) continue;
    manageFactory(w, e);
    // Contribute the factory's own build power to whatever it is producing.
    const bt = w.buildTargetEnt[e];
    if (bt >= 0 && w.isAlive(bt) && w.underConstruction(bt)) bp[bt] += def.buildPower;
  }

  // --- Per-owner passive income, caps, converter, overflow. ---
  for (const owner of [OWNER_PLAYER, OWNER_ENEMY]) {
    const eco = w.eco[owner];
    let metalCap = 0;
    let energyCap = 0;
    let mProd = 0;
    let eProd = 0;
    let eUpkeep = 0;
    let converterCount = 0;
    let convDraw = 0;
    let convOut = 0;
    for (let e = 0; e < w.count; e++) {
      if (!w.alive[e] || w.owner[e] !== owner || w.underConstruction(e)) continue;
      const def = w.def(e);
      const ec = def.econ;
      if (!ec) continue;
      if (ec.storageMetal) metalCap += ec.storageMetal;
      if (ec.storageEnergy) energyCap += ec.storageEnergy;
      if (ec.metalYield) mProd += ec.metalYield;
      if (ec.energyYield) eProd += ec.energyYield;
      if (ec.windTurbine) eProd += w.windCurrent;
      if (ec.energyUpkeep) eUpkeep += ec.energyUpkeep;
      if (ec.converter) {
        converterCount++;
        convDraw += ec.converterEnergyDraw ?? 0;
        convOut += ec.converterMetalOut ?? 0;
      }
    }
    if (metalCap < 50) metalCap = 50;
    if (energyCap < 50) energyCap = 50;
    eco.metalCap = metalCap;
    eco.energyCap = energyCap;

    // Converter auto-toggle: run when energy buffer is healthy and metal has room.
    const converterOn =
      converterCount > 0 && eco.energy / energyCap > 0.55 && eco.metal < metalCap * 0.98;
    setConverterFlags(w, owner, converterOn);
    const activeConvDraw = converterOn ? convDraw : 0;
    const activeConvOut = converterOn ? convOut : 0;

    // Apply passive rates to storage.
    const netE = eProd - eUpkeep - activeConvDraw;
    const netM = mProd + activeConvOut;
    eco.energy += netE * DT;
    eco.metal += netM * DT;

    eco.energyWasted = 0;
    eco.metalWasted = 0;
    if (eco.energy > energyCap) {
      eco.energyWasted = (eco.energy - energyCap) / DT;
      eco.energy = energyCap;
    }
    if (eco.energy < 0) eco.energy = 0;
    if (eco.metal > metalCap) {
      eco.metalWasted = (eco.metal - metalCap) / DT;
      eco.metal = metalCap;
    }
    if (eco.metal < 0) eco.metal = 0;

    eco.metalIncome = mProd + activeConvOut;
    eco.energyIncome = eProd;
    // expense accumulators (construction added below; weapons deducted at fire).
    eco.metalExpense = 0;
    eco.energyExpense = eUpkeep + activeConvDraw;
  }

  // --- Construction demand per owner. ---
  const demandM = [0, 0, 0];
  const demandE = [0, 0, 0];
  for (let t = 0; t < w.count; t++) {
    if (bp[t] <= 0) continue;
    if (!w.isAlive(t) || !w.underConstruction(t)) {
      bp[t] = 0;
      continue;
    }
    const def = w.def(t);
    // Whoever is building it owns the drain. Under-construction entity carries its owner.
    const owner = w.owner[t];
    const rate = def.buildTime > 0 ? (bp[t] / 100) / def.buildTime : 1; // fraction/sec
    demandM[owner] += def.metalCost * rate;
    demandE[owner] += def.energyCost * rate;
  }

  // --- Repair demand per owner (energy only; TA-lineage: no metal). ---
  // Repairing a completed-but-damaged entity restores health at the same rate
  // construction would rebuild it, and draws the SAME energy per second that
  // construction would — but never any metal. (Resuming an under-construction
  // entity is handled by buildPowerOn above and DOES cost its remaining metal.)
  for (let t = 0; t < w.count; t++) {
    if (rpair[t] <= 0) continue;
    if (!w.isAlive(t) || w.underConstruction(t) || w.health[t] >= w.maxHealth[t]) {
      rpair[t] = 0;
      continue;
    }
    const def = w.def(t);
    const owner = w.owner[t];
    const rate = def.buildTime > 0 ? (rpair[t] / 100) / def.buildTime : 1; // fraction/sec
    demandE[owner] += def.energyCost * rate;
  }

  const fundFactor = [1, 1, 1];
  for (const owner of [OWNER_PLAYER, OWNER_ENEMY]) {
    const eco = w.eco[owner];
    const needM = demandM[owner] * DT;
    const needE = demandE[owner] * DT;
    let f = 1;
    if (needM > 0) f = Math.min(f, eco.metal / needM);
    if (needE > 0) f = Math.min(f, eco.energy / needE);
    if (f < 0) f = 0;
    if (f > 1) f = 1;
    fundFactor[owner] = f;
    eco.stalled = f < 0.995 && (needM > 0 || needE > 0);
    eco.metalExpense += demandM[owner] * f;
    eco.energyExpense += demandE[owner] * f;
  }

  // --- Advance construction with the funded factor; complete finished builds. ---
  for (let t = 0; t < w.count; t++) {
    if (bp[t] <= 0) continue;
    if (!w.isAlive(t) || !w.underConstruction(t)) continue;
    const def = w.def(t);
    const owner = w.owner[t];
    const f = fundFactor[owner];
    const rate = def.buildTime > 0 ? (bp[t] / 100) / def.buildTime : 1;
    let dprog = rate * f * DT;
    const remaining = 1 - w.buildProgress[t];
    if (dprog > remaining) dprog = remaining;
    const frac = rate > 0 && f > 0 ? dprog / (rate * DT) : 0;
    // Deduct proportional cost.
    const eco = w.eco[owner];
    eco.metal -= def.metalCost * rate * DT * frac;
    eco.energy -= def.energyCost * rate * DT * frac;
    if (eco.metal < 0) eco.metal = 0;
    if (eco.energy < 0) eco.energy = 0;
    w.buildProgress[t] += dprog;
    // Heal toward full as it builds.
    w.health[t] = Math.max(w.health[t], def.maxHealth * (0.05 + 0.95 * w.buildProgress[t]));
    if (w.buildProgress[t] >= 0.9999) completeBuild(w, t);
  }

  // --- Advance repair with the funded factor (energy only; heal, no metal). ---
  for (let t = 0; t < w.count; t++) {
    if (rpair[t] <= 0) continue;
    if (!w.isAlive(t) || w.underConstruction(t)) continue;
    const missing = w.maxHealth[t] - w.health[t];
    if (missing <= 0) continue;
    const def = w.def(t);
    const owner = w.owner[t];
    const f = fundFactor[owner];
    const rate = def.buildTime > 0 ? (rpair[t] / 100) / def.buildTime : 1; // fraction/sec
    let dHealth = rate * f * DT * def.maxHealth; // HP restored this tick
    if (dHealth > missing) dHealth = missing;
    // Energy cost is proportional to the fraction of a full build we just healed.
    const frac = def.maxHealth > 0 ? dHealth / def.maxHealth : 0;
    const eco = w.eco[owner];
    eco.energy -= def.energyCost * frac;
    if (eco.energy < 0) eco.energy = 0;
    w.health[t] += dHealth;
  }

  // --- Reclaim yields (not throttled; reclaim is pure income). ---
  for (let t = 0; t < w.count; t++) {
    if (rp[t] <= 0) continue;
    if (!w.isAlive(t)) {
      rp[t] = 0;
      continue;
    }
    const rate = rp[t] * RECLAIM_METAL_PER_BP * DT;
    const owner = reclaimOwnerFor(w, t);
    if (owner < 0) continue;
    const eco = w.eco[owner];
    let mLeft = w.reclaimMetalStore[t];
    let eLeft = w.reclaimEnergyStore[t];
    const total = mLeft + eLeft;
    if (total <= 0) {
      w.kill(t);
      continue;
    }
    // Split the reclaim step across whatever the target holds.
    const take = Math.min(rate, total);
    const mShare = total > 0 ? (mLeft / total) * take : 0;
    const eShare = total > 0 ? (eLeft / total) * take : 0;
    mLeft -= mShare;
    eLeft -= eShare;
    eco.metal = Math.min(eco.metalCap, eco.metal + mShare);
    eco.energy = Math.min(eco.energyCap, eco.energy + eShare);
    eco.metalIncome += mShare / DT;
    eco.energyIncome += eShare / DT;
    w.reclaimMetalStore[t] = mLeft;
    w.reclaimEnergyStore[t] = eLeft;
    // Shrink health for visual + destroy when spent.
    w.health[t] = w.maxHealth[t] * (mLeft + eLeft > 0 ? (mLeft + eLeft) / total : 0);
    if (mLeft + eLeft <= 0.001) w.kill(t);
  }
}

function reclaimOwnerFor(w: World, target: number): number {
  // Find a builder currently reclaiming this target to credit resources.
  for (let e = 0; e < w.count; e++) {
    if (w.alive[e] && w.buildTargetEnt[e] === target && w.def(e).buildPower > 0 && w.action[e] === ACT_RECLAIM) {
      return w.owner[e];
    }
  }
  return -1;
}

function setConverterFlags(w: World, owner: number, on: boolean): void {
  for (let e = 0; e < w.count; e++) {
    if (!w.alive[e] || w.owner[e] !== owner) continue;
    const def = w.def(e);
    if (def.econ?.converter) {
      if (on) w.flags[e] |= F_CONVERTER_ON;
      else w.flags[e] &= ~F_CONVERTER_ON;
    }
  }
}

function manageFactory(w: World, f: number): void {
  const bt = w.buildTargetEnt[f];
  const producing = bt >= 0 && w.isAlive(bt) && w.underConstruction(bt);
  if (producing) return;
  // Previous item finished or was destroyed -> dequeue it.
  if (bt >= 0) {
    if (w.factoryQueue[f].length > 0) w.factoryQueue[f].shift();
    w.buildTargetEnt[f] = -1;
  }
  // Start next queued unit.
  const queue = w.factoryQueue[f];
  if (queue.length === 0) return;
  const defId = queue[0];
  const fdef = w.def(f);
  const exitX = w.posX[f];
  const exitY = w.posY[f] + fdef.radius + 12;
  const ghost = w.spawn(defId, w.owner[f], exitX, exitY, { underConstruction: true, heading: w.heading[f] });
  if (ghost < 0) return;
  w.rallyX[ghost] = w.rallyX[f];
  w.rallyY[ghost] = w.rallyY[f];
  w.buildTargetEnt[f] = ghost;
}

function completeBuild(w: World, t: number): void {
  const def = w.def(t);
  w.flags[t] &= ~F_UNDER_CONSTRUCTION;
  w.buildProgress[t] = 1;
  w.health[t] = def.maxHealth;
  w.action[t] = 0;
  // Factory-produced mobile unit: send it to the rally point.
  if (w.kind[t] === 0) {
    const rx = w.rallyX[t];
    const ry = w.rallyY[t];
    if (Math.hypot(rx - w.posX[t], ry - w.posY[t]) > 8) {
      w.orders[t].length = 0;
      w.orders[t].push({ kind: "move", x: rx, y: ry, attackMove: false });
    }
  }
}

/** Bind a completed/placed extractor to the metal spot nearest its position. */
export function bindExtractorToSpot(w: World, e: number): void {
  const spots = w.gmap.def.metalSpots;
  let best = -1;
  let bestD = 1e9;
  for (let i = 0; i < spots.length; i++) {
    if (w.metalSpotOccupant[i] >= 0 && w.metalSpotOccupant[i] !== e) continue;
    const d = Math.hypot(spots[i].x - w.posX[e], spots[i].y - w.posY[e]);
    if (d < bestD) {
      bestD = d;
      best = i;
    }
  }
  if (best >= 0 && bestD < 40) {
    w.metalSpotIdx[e] = best;
    w.metalSpotOccupant[best] = e;
  }
}
